import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
  NgZone,
  ChangeDetectorRef,
  Renderer2,
  inject
} from '@angular/core';


import { SingleSpaProps } from "../../../single-spa/single-spa-props";
import { AuthService, construirMenuCacheKey } from "../../services/auth.service";
import { MenuCacheService } from "../../services/menu-cache.service";
import {
  MenuWatchdogRecoveryLevel,
  MenuWatchdogService,
} from "../../services/menu-watchdog.service";
import {
  canonicalMenuRaw,
  normalizeMenuCompradorArray,
  readMenuCompradorChildren,
} from "../../utils/menu-comprador-canonical.util";
import { EmpresaBrandig } from "../../interfaces/empresa-brandig.interface";
import { DominioOrigen } from "../../enums/dominio-origen";
import { AmbienteSitio } from "../../enums/ambiente-sitio";
import { environment } from "../../../environments/environment";
import { HelperService } from "../../services/helper.service";
import { Subject, takeUntil } from "rxjs";
import { MenuBffService } from "src/app/services/menubff-service";
import { MenuComprador, MenuCompradorResponse } from '../../interfaces/menu-comprador.interface';
import { ShellIframeService } from '../../services/shell-iframe.service';
import { TagManagerService } from '../../services/tag-manager.service';
import { UIInteractionData } from '../../interfaces/tag-manager.types';


export interface SubMenu {
  label: string;
  redirect?: string;
  children?: SubMenu[];
}

export interface MenuItem {
  label: string;
  redirect?: string;
  subMenu?: SubMenu[];
}

@Component({
  selector: "app-menu-horizontal",
  standalone: true,
  imports: [],
  templateUrl: './menu-horizontal.component.html',
  styleUrl: './menu-horizontal.component.scss'
})
export class MenuHorizontalComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  /** Must stay in sync with `@media (max-width: …)` in component SCSS */
  private static readonly MOBILE_LAYOUT_MAX_WIDTH_PX = 768;

  /**
   * Nombre estable del menú de aplicaciones del shell (`menu-aplicaciones-new`).
   */
  private static readonly APPLICATIONS_MENU_HOST_KEY = 'menu-aplicaciones-new';

  /** Evento contractual para que el shell cierre overlays que compiten con el menú MF. */
  private static readonly DISMISS_POPOVERS_EVENT = 'menu-asteric:dismiss-popovers';

  /** Límite de invocaciones a `Fallback()` por ventana de sesión (anti-loop). */
  private static readonly FALLBACK_MAX_ATTEMPTS = 3;
  /** Mínimo entre intentos consecutivos de `Fallback()` (ms). */
  private static readonly FALLBACK_MIN_RETRY_GAP_MS = 4000;
  /** Tras este tiempo se reinicia el contador de intentos (ms). */
  private static readonly FALLBACK_WINDOW_MS = 60000;

  /**
   * Selectores para el panel del MF selector-apps (`app-menu`): clase en un `div`, no tag custom.
   * Orden: el más específico primero.
   */
  private static readonly APPLICATIONS_MENU_HOST_SELECTORS: readonly string[] = [
    '.menu-container.menu-aplicaciones-new',
    `#${MenuHorizontalComponent.APPLICATIONS_MENU_HOST_KEY}`,
    MenuHorizontalComponent.APPLICATIONS_MENU_HOST_KEY,
    `.${MenuHorizontalComponent.APPLICATIONS_MENU_HOST_KEY}`
  ];

  /** Raíz típica del MF en single-spa (el `:` del id obliga selector por atributo). */
  private static readonly SELECTOR_APPS_MICROFRONT_ID = 'single-spa-application:SelectorApps';

  /** MF header con selector de empresa / usuario (`app-selector-empresa`). */
  private static readonly HEADER_SITE_MICROFRONT_ID = 'single-spa-application:HeaderSite';

  /** Scope CSS para elementos dentro del MF HeaderSite (`:` en id fuerza `[id="..."]`). */
  private static headerSiteSelectorEmpresaScopes(): readonly string[] {
    return [`[id="${MenuHorizontalComponent.HEADER_SITE_MICROFRONT_ID}"] app-selector-empresa`];
  }

  private static findApplicationsMenuHost(): HTMLElement | null {
    if (typeof document === 'undefined') {
      return null;
    }
    for (const selector of MenuHorizontalComponent.APPLICATIONS_MENU_HOST_SELECTORS) {
      try {
        const node = document.querySelector(selector);
        if (node instanceof HTMLElement) {
          return node;
        }
      } catch {
        continue;
      }
    }
    return null;
  }

  private static isRoughlyVisibleElement(el: HTMLElement): boolean {
    const s = window.getComputedStyle(el);
    if (
      s.display === 'none' ||
      s.visibility === 'hidden' ||
      Number.parseFloat(s.opacity) <= 0.01
    ) {
      return false;
    }
    const r = el.getBoundingClientRect();
    const minPx = 4;
    return r.width >= minPx && r.height >= minPx;
  }

  private static findSelectorAppsTriggerButton(): HTMLElement | null {
    if (typeof document === 'undefined') {
      return null;
    }
    return (
      (document.querySelector(
        `[id="${MenuHorizontalComponent.SELECTOR_APPS_MICROFRONT_ID}"] app-selector-apps .div-selector-apps-new button:not([hidden])`
      ) as HTMLElement | null) ??
      (document.querySelector(
        'app-selector-apps .div-selector-apps-new button.mat-icon-button:not([hidden])'
      ) as HTMLElement | null)
    );
  }

  /** Estado aparentemente abierto sin cerrar ni mutar DOM. */
  private static selectorAppsPopoverAppearsOpen(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }

    const trigger = MenuHorizontalComponent.findSelectorAppsTriggerButton();
    if (!trigger) {
      return false;
    }

    const panel = MenuHorizontalComponent.findApplicationsMenuHost();
    const grid = document.querySelector(
      '.menu-container.menu-aplicaciones-new .menu-grid, .menu-aplicaciones-new .menu-grid'
    ) as HTMLElement | null;

    const firstApp = grid?.querySelector('.menu-app') as HTMLElement | null;

    return (
      trigger.getAttribute('aria-expanded') === 'true' ||
      (panel instanceof HTMLElement && MenuHorizontalComponent.isRoughlyVisibleElement(panel)) ||
      (!!grid &&
        MenuHorizontalComponent.isRoughlyVisibleElement(grid) &&
        !!firstApp &&
        MenuHorizontalComponent.isRoughlyVisibleElement(firstApp))
    );
  }

  /** Panel usuario del header abierto aparentemente. */
  private static headerSiteUserPopoverAppearsOpen(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }

    const userPanel = MenuHorizontalComponent.queryHeaderSiteSelectorEmpresa(
      '.contenedor-popover-usuario'
    );
    const userTrigger = MenuHorizontalComponent.queryHeaderSiteSelectorEmpresa('p-avatar.avatar-user');

    const userEmailLine = userPanel?.querySelector('h6.textoNegrito') as HTMLElement | null;
    const fallbackInner =
      userPanel instanceof HTMLElement
        ? (userPanel.querySelector('.opciones-usuario') as HTMLElement | null)
        : null;

    const hasVisibleUserContent =
      (userEmailLine != null &&
        MenuHorizontalComponent.isRoughlyVisibleElement(userEmailLine)) ||
      (fallbackInner != null && MenuHorizontalComponent.isRoughlyVisibleElement(fallbackInner));

    return (
      userTrigger != null &&
      userPanel instanceof HTMLElement &&
      MenuHorizontalComponent.isRoughlyVisibleElement(userPanel) &&
      hasVisibleUserContent
    );
  }

  /**
   * Panel "Cambiar Empresa" (`contenedor-popover-selector`), a veces abierto sin `aria-expanded` en el trigger.
   */
  private static headerSiteCompanySelectorPanelAppearsOpen(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }

    const panel = MenuHorizontalComponent.queryHeaderSiteSelectorEmpresa(
      '.contenedor-popover-selector'
    );
    if (!(panel instanceof HTMLElement) || !MenuHorizontalComponent.isRoughlyVisibleElement(panel)) {
      return false;
    }

    const body = panel.querySelector('.contenedor-body-selector') as HTMLElement | null;
    if (!body || !MenuHorizontalComponent.isRoughlyVisibleElement(body)) {
      return false;
    }

    return !!(
      panel.querySelector('.contenedor-body-selector p-select') ??
      panel.querySelector('.contenedor-body-selector .titulo')
    );
  }

  private static headerSiteCompanySelectorAppearsOpen(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }

    if (MenuHorizontalComponent.headerSiteCompanySelectorPanelAppearsOpen()) {
      return true;
    }

    const companyTrigger = MenuHorizontalComponent.queryHeaderSiteSelectorEmpresa(
      '.btn-selector button:not([disabled])'
    );
    return (
      companyTrigger != null &&
      companyTrigger.getAttribute('aria-expanded') === 'true'
    );
  }

  /** Cierra el popover de cambio de empresa (X, o toggle del botón cabecera). */
  private static tryCloseHeaderSiteCompanySelectorViaTrigger(): void {
    if (typeof document === 'undefined') {
      return;
    }

    if (!MenuHorizontalComponent.headerSiteCompanySelectorAppearsOpen()) {
      return;
    }

    const scopedPanel = MenuHorizontalComponent.queryHeaderSiteSelectorEmpresa(
      '.contenedor-popover-selector'
    );
    if (
      scopedPanel instanceof HTMLElement &&
      MenuHorizontalComponent.isRoughlyVisibleElement(scopedPanel)
    ) {
      const closeCtl = scopedPanel.querySelector('.contenido-close') as HTMLElement | null;
      if (closeCtl && MenuHorizontalComponent.isRoughlyVisibleElement(closeCtl)) {
        closeCtl.click();
        return;
      }
    }

    let floatingPanel = document.querySelector(
      `app-selector-empresa .contenedor-popover-selector`
    ) as HTMLElement | null;
    floatingPanel ??= document.querySelector('.contenedor-popover-selector') as HTMLElement | null;
    if (floatingPanel && MenuHorizontalComponent.isRoughlyVisibleElement(floatingPanel)) {
      const closeCtl = floatingPanel.querySelector('.contenido-close') as HTMLElement | null;
      if (closeCtl && MenuHorizontalComponent.isRoughlyVisibleElement(closeCtl)) {
        closeCtl.click();
        return;
      }
    }

    MenuHorizontalComponent.queryHeaderSiteSelectorEmpresa('.btn-selector button:not([disabled])')?.click();
  }

  private static headerSiteOverlaysAppearOpen(): boolean {
    return (
      MenuHorizontalComponent.headerSiteUserPopoverAppearsOpen() ||
      MenuHorizontalComponent.headerSiteCompanySelectorAppearsOpen()
    );
  }

  private static foreignHostOverlaysAppearOpen(): boolean {
    return (
      MenuHorizontalComponent.selectorAppsPopoverAppearsOpen() ||
      MenuHorizontalComponent.headerSiteOverlaysAppearOpen()
    );
  }

  /**
   * El menú de aplicaciones es otro MF (SelectorApps / `app-selector-apps`): no recibe CustomEvent si nadie escucha,
   * y Angular/Material a menudo ignoran pointer sintéticos. Un `HTMLElement.click()` en el trigger suele ejecutar el toggle.
   */
  private static tryCloseSelectorAppsPopoverViaTrigger(): void {
    if (typeof document === 'undefined') {
      return;
    }

    if (!MenuHorizontalComponent.selectorAppsPopoverAppearsOpen()) {
      return;
    }

    const trigger = MenuHorizontalComponent.findSelectorAppsTriggerButton();
    trigger?.click();
  }

  /**
   * Popovers del MF `HeaderSite` / `app-selector-empresa`: usuario (`.contenedor-popover-usuario`), selector empresa (`contenedor-popover-selector` / `.btn-selector`).
   * viven fuera del menú Angular; mismo patrón que SelectorApps — `click()` programático cuando el estado parece abierto.
   */
  private static queryHeaderSiteSelectorEmpresa(selectorTail: string): HTMLElement | null {
    if (typeof document === 'undefined') {
      return null;
    }
    for (const scope of MenuHorizontalComponent.headerSiteSelectorEmpresaScopes()) {
      try {
        const hit = document.querySelector(`${scope} ${selectorTail}`);
        if (hit instanceof HTMLElement) {
          return hit;
        }
      } catch {
        continue;
      }
    }
    try {
      const loose = document.querySelector(`app-selector-empresa ${selectorTail}`);
      return loose instanceof HTMLElement ? loose : null;
    } catch {
      return null;
    }
  }

  private static tryCloseHeaderSiteSelectorEmpresaOverlaysViaTrigger(): void {
    if (typeof document === 'undefined') {
      return;
    }

    if (MenuHorizontalComponent.headerSiteUserPopoverAppearsOpen()) {
      const userTrigger =
        MenuHorizontalComponent.queryHeaderSiteSelectorEmpresa('p-avatar.avatar-user');
      userTrigger?.click();
    }

    if (MenuHorizontalComponent.headerSiteCompanySelectorAppearsOpen()) {
      MenuHorizontalComponent.tryCloseHeaderSiteCompanySelectorViaTrigger();
    }
  }

  @Input() menuItems: MenuItem[] = [];
  @Input() disableScroll: boolean = false; // Option to disable scroll completely
  @Input() microfrontendProps: SingleSpaProps = {}; // Props recibidas del microfrontend
  @Output() bannerIdsChange = new EventEmitter<string[]>();

  // Mobile menu properties — use matchMedia so JS matches `@media (max-width: 768px)` (Chrome/iOS can diverge from innerWidth)
  mobileBreakpoint: number = MenuHorizontalComponent.MOBILE_LAYOUT_MAX_WIDTH_PX;
  /** Sync with viewport on construction to avoid orange bar FOUC before ngOnInit */
  isMobile: boolean = MenuHorizontalComponent.matchesMaxWidthMedia(
    MenuHorizontalComponent.MOBILE_LAYOUT_MAX_WIDTH_PX
  );
  isMobileMenuOpen: boolean = false;

  /** Escucha el mismo criterio que el SCSS responsive (evita UI vacía si innerWidth y media queries difieren). */
  private mobileViewportMql: MediaQueryList | null = null;
  private readonly onMobileViewportMqlChange = (): void => {
    this.ngZone.run(() => {
      this.checkScreenSize();
      this.cdr.detectChanges();
    });
  };

  // Props del microfrontend
  usuario: string = "";
  rol: string = "";
  token: string = "";
  whitelabel: string = "";
  Redirect: any = null;
  urlSitio: string = '';
  onLogout?: () => void;

  // Tema actual aplicado
  currentTheme: string = "ic";

  // Estado de carga del menú
  isLoadingMenu: boolean = false;
  menuError: string | null = null;
  /** Placeholders para *ngFor del skeleton (solo primer nivel). */
  skeletonItems: number[] = [1, 2, 3, 4, 5, 6];

  // Configuración WhiteLabel
  empresaBranding: EmpresaBrandig | null = null;
  logoIsotipoUrl: string | null = null;
  logoPlataformaUrl: string | null = null;

  /** Índices del ítem expandido en el menú móvil (submenús por clic, sin depender de :hover). */
  mobileExpandedParentIndex: number | null = null;
  mobileExpandedSubParentIndex: number | null = null;

  // Propiedades para comunicación con ventana padre
  private pendingRedirectUrl: string | null = null;

  /** Cancela el subscribe de SSO al destruir el componente. */
  private readonly destroy$ = new Subject<void>();
  private readonly tagManager = inject(TagManagerService);
  private menuPageViewLogged = false;

  private getGtmEmpresaNombre(): string | null {
    return this.empresaBranding?.nombre
      ?? this.empresaBranding?.titulo
      ?? this.microfrontendProps.companyName?.trim()
      ?? null;
  }

  private trackMenuInteraction(
    handler: string,
    interactionData: UIInteractionData,
    onComplete?: () => void
  ): void {
    this.tagManager.logUIInteraction(interactionData, {
      onComplete,
      empresa_nombre: this.getGtmEmpresaNombre()
    });
  }

  /** Registra page_view una vez cuando el menú se pinta con ítems (caché o API). */
  private trackMenuPageView(itemsCount: number): void {
    if (this.menuPageViewLogged || itemsCount === 0) {
      return;
    }

    this.menuPageViewLogged = true;
    this.tagManager.logPageView(
      {
        page_title: typeof document !== 'undefined' ? document.title : 'Menu Asteric',
        page_path: typeof window !== 'undefined'
          ? `${window.location.pathname}${window.location.search}${window.location.hash}`
          : undefined,
        component_name: 'Menu Horizontal',
        menu_items_count: itemsCount,
      },
      this.getGtmEmpresaNombre()
    );
  }

  // Datos de prueba (mock data) con 20 opciones de menú para cuando el API no responda
  mockMenuItems: MenuItem[] = [
  ];

  constructor(
    private authService: AuthService,
    private menuCacheService: MenuCacheService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private helperService: HelperService,
    private renderer: Renderer2,
    private menuService: MenuBffService,
    private shellIframeService: ShellIframeService,
    private menuWatchdog: MenuWatchdogService
  ) {
    // El menú se cargará desde el endpoint en ngOnInit
    // Solo usar menú por defecto si se proporciona explícitamente

    // Exponer métodos de debug globalmente para acceso desde consola
    (window as any).debugMenuData = () => this.debugMicrofrontendData();
    (window as any).changeMenuTheme = (theme: string) =>
      this.changeTheme(theme);
    (window as any).getMenuThemeInfo = () => this.getCurrentThemeInfo();
    (window as any).usarDatosDePrueba = () => this.usarDatosDePrueba();
    (window as any).recargarMenu = () => this.recargarMenu();
    (window as any).getMenuInfo = () => this.getMenuInfo();

    this.checkScreenSize();
    this.attachMobileViewportMediaListener();
  }

  ngOnInit(): void {
    this.checkScreenSize();
    this.processMicrofrontendProps();
    this.obtenerPermisosYCargarMenu();

    this.cargarConfiguracionWhiteLabel();
    this.startMenuWatchdog();

    this.shellIframeService.recargaMenuComprador$
      .pipe(takeUntil(this.destroy$))
      .subscribe((opciones) => {
        this.ngZone.run(() => this.cargarMenuComprador(opciones));
      });

    this.authService.tokenPermisosCambios$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.ngZone.run(() => this.cargarMenuComprador({ omitirCacheLocal: true }));
      });
  }

  /**
   * `IdPais` del shell; solo se usa en API/caché si coincide con ISO alpha-2/3 (letras).
   */
  private getIdPaisNormalizadoFromProps(): string | undefined {
    const raw = this.microfrontendProps?.IdPais;
    if (raw == null || typeof raw !== 'string') {
      return undefined;
    }
    const t = raw.trim();
    if (!/^[A-Za-z]{2,3}$/.test(t)) {
      return undefined;
    }
    return t.toUpperCase();
  }

  /**
   * 1) Cache local (si hay clave) para pintar al instante.
   * 2) SSO: `AutenticarNuevoProducto` con identificador codificado → respuesta con `tokenPermisos` (y opcional access/refresh/id).
   * 3) Cargar menú una vez persistidos los datos en cookies.
   */
  private obtenerPermisosYCargarMenu(): void {
    if (this.menuItems.length === 0) {
      this.isLoadingMenu = true;
      this.pintarLocalStorageFallback("pre-sso");
    }
    this.ensureTokenPermisosYHacer(() => {
      this.cargarMenuComprador();
    });
  }


  private getFallbackFlagKey(): string | null {
    const idPaisMenu = this.getIdPaisNormalizadoFromProps();
    const base = this.authService.getFallbackExecutedCacheKey(idPaisMenu);
    if (!base) {
      return null;
    }
    return this.isRegistroActivo() ? `${base}-registro` : base;
  }

  /** Estado persistido en `sessionStorage` bajo `getFallbackFlagKey()`. */
  private readShellFallbackPolicy(): {
    attempts: number;
    firstAttemptAt: number;
    lastAttemptAt: number;
  } | null {
    const key = this.getFallbackFlagKey();
    if (!key || typeof sessionStorage === "undefined") {
      return null;
    }
    const raw = sessionStorage.getItem(key);
    if (raw === null || raw === "") {
      return null;
    }
    if (raw === "true") {
      return {
        attempts: MenuHorizontalComponent.FALLBACK_MAX_ATTEMPTS,
        firstAttemptAt: Date.now(),
        lastAttemptAt: Date.now(),
      };
    }
    try {
      const o = JSON.parse(raw) as unknown;
      if (o === null || typeof o !== "object") {
        return null;
      }
      const rec = o as Record<string, unknown>;
      const attempts = Number(rec["attempts"]);
      const firstAttemptAt = Number(rec["firstAttemptAt"]);
      const lastAttemptAt = Number(rec["lastAttemptAt"]);
      if (
        !Number.isFinite(attempts) ||
        !Number.isFinite(firstAttemptAt) ||
        !Number.isFinite(lastAttemptAt)
      ) {
        return null;
      }
      return { attempts, firstAttemptAt, lastAttemptAt };
    } catch {
      return null;
    }
  }

  private writeShellFallbackPolicy(
    key: string,
    state: { attempts: number; firstAttemptAt: number; lastAttemptAt: number },
  ): void {
    if (typeof sessionStorage === "undefined") {
      return;
    }
    try {
      sessionStorage.setItem(key, JSON.stringify(state));
    } catch {
      /* quota / privado */
    }
  }

  /**
   * Reserva un intento de `Fallback()` si la política anti-loop lo permite.
   * Efecto lateral: persiste intento en `sessionStorage` sólo si retorna `ok`.
   */
  private tryConsumeShellFallbackAttempt(): { ok: boolean; reason: string | null } {
    const key = this.getFallbackFlagKey();
    if (!key) {
      return { ok: false, reason: "no_fallback_key" };
    }
    const now = Date.now();
    let s = this.readShellFallbackPolicy();
    if (!s) {
      s = { attempts: 0, firstAttemptAt: now, lastAttemptAt: 0 };
    }
    if (now - s.firstAttemptAt > MenuHorizontalComponent.FALLBACK_WINDOW_MS) {
      s = { attempts: 0, firstAttemptAt: now, lastAttemptAt: 0 };
    }
    if (s.attempts >= MenuHorizontalComponent.FALLBACK_MAX_ATTEMPTS) {
      return { ok: false, reason: "max_attempts" };
    }
    if (
      s.lastAttemptAt > 0 &&
      now - s.lastAttemptAt < MenuHorizontalComponent.FALLBACK_MIN_RETRY_GAP_MS
    ) {
      return { ok: false, reason: "cooldown" };
    }
    s.attempts += 1;
    s.lastAttemptAt = now;
    if (s.attempts === 1) {
      s.firstAttemptAt = now;
    }
    this.writeShellFallbackPolicy(key, s);
    return { ok: true, reason: null };
  }

  /** Solo lectura para consola / `getMenuInfo` (no consume intento). */
  private peekShellFallbackPolicyForDebug(): {
    key: string | null;
    wouldBlockReason: string | null;
    attempts: number;
    maxAttempts: number;
    windowMs: number;
    minGapMs: number;
  } {
    const key = this.getFallbackFlagKey();
    const maxAttempts = MenuHorizontalComponent.FALLBACK_MAX_ATTEMPTS;
    const windowMs = MenuHorizontalComponent.FALLBACK_WINDOW_MS;
    const minGapMs = MenuHorizontalComponent.FALLBACK_MIN_RETRY_GAP_MS;
    if (!key) {
      return {
        key: null,
        wouldBlockReason: "no_fallback_key",
        attempts: 0,
        maxAttempts,
        windowMs,
        minGapMs,
      };
    }
    const now = Date.now();
    let s = this.readShellFallbackPolicy();
    if (!s) {
      return {
        key,
        wouldBlockReason: null,
        attempts: 0,
        maxAttempts,
        windowMs,
        minGapMs,
      };
    }
    if (now - s.firstAttemptAt > windowMs) {
      return {
        key,
        wouldBlockReason: null,
        attempts: 0,
        maxAttempts,
        windowMs,
        minGapMs,
      };
    }
    if (s.attempts >= maxAttempts) {
      return {
        key,
        wouldBlockReason: "max_attempts",
        attempts: s.attempts,
        maxAttempts,
        windowMs,
        minGapMs,
      };
    }
    if (s.lastAttemptAt > 0 && now - s.lastAttemptAt < minGapMs) {
      return {
        key,
        wouldBlockReason: "cooldown",
        attempts: s.attempts,
        maxAttempts,
        windowMs,
        minGapMs,
      };
    }
    return {
      key,
      wouldBlockReason: null,
      attempts: s.attempts,
      maxAttempts,
      windowMs,
      minGapMs,
    };
  }

  /**
   * Codifica props single-spa + llama SSO una sola vez por oleada (dedupe en `AuthService.ensureTokenPermisos$`).
   */
  private ensureTokenPermisosYHacer(callback: () => void): void {
    const idCambioEmpresa = environment.idCambioEmpresa;
    const dominio =
      this.microfrontendProps.Dominio != null && this.microfrontendProps.Dominio !== ''
        ? String(this.microfrontendProps.Dominio)
        : '';
    const base64 = this.helperService.enviarIdentificadorCodificado(
      this.microfrontendProps.IdEmpresa ?? 0,
      this.microfrontendProps.IdUsuario ?? '',
      this.microfrontendProps.IdOrg ?? 0,
      dominio
    );

    this.authService
      .ensureTokenPermisos$(idCambioEmpresa, base64)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.ngZone.run(callback);
        },
        error: () => {
          this.ngZone.run(callback);
        }
      });
  }

  // Track hover state for menu items
  hoveredItem: number | null = null;
  hoveredSubItem: number | null = null;
  submenuPosition: { top: number; left: number } = { top: 0, left: 0 };
  nestedSubmenuPosition: { top: number; left: number } = { top: 0, left: 0 };
  /** `null`: usa solo el fallback CSS del portal (`calc(100vh - …)`). */
  nestedSubmenuMaxHeight: number | null = null;

  // Track if submenu is active to maintain parent hover state
  isSubmenuActive: boolean = false;

  // Track if nested submenu is active to maintain second level hover state
  isNestedSubmenuActive: boolean = false;

  // Timer for delayed hiding
  private hideTimer: any = null;
  private readonly HIDE_DELAY = 250; // 250 delay before hiding (increased for better navigation)

  /** Último `<li>` de 2.º nivel con hover; para recalcular anidado en resize/scroll si aplica. */
  private lastHoveredSubmenuLi: HTMLElement | null = null;

  private nestedSubmenuViewportAdjustRaf: number | null = null;

  private readonly NESTED_VIEWPORT_MARGIN_PX = 8;
  /** Separación mínima entre el borde inferior del menú horizontal `.menu-container` y el tercer nivel. */
  private readonly NESTED_SUBMENU_MENU_BAR_GAP_PX = 8;

  /** El scroll real está en .menu-container; el listener debe ir ahí, no en el nav. */
  private scrollableMenuContainer: HTMLElement | null = null;
  private readonly onMenuContainerScroll = (): void => {
    this.ngZone.run(() => {
      this.checkScrollState();
      this.refreshSubmenuPositionIfOpen();
    });
  };

  private menuListResizeObserver: ResizeObserver | null = null;

  // Track if submenu position is locked
  private submenuPositionLocked: boolean = false;

  /** Referencia al ítem `li.menu-item` con hover (reposicionar submenú al hacer scroll/resize). */
  private lastHoveredMenuItemLi: HTMLElement | null = null;

  // Navigation arrows
  @ViewChild('menuNav', { static: false }) menuNav!: ElementRef;
  @ViewChild('submenuPortal', { static: false }) submenuPortal?: ElementRef<HTMLElement>;
  showLeftArrow: boolean = false;
  showRightArrow: boolean = false;
  canScrollLeft: boolean = false;
  canScrollRight: boolean = false;
  leftArrowPosition: { top: number; left: number } = { top: 0, left: 0 };
  rightArrowPosition: { top: number; right: number } = { top: 0, right: 0 };

  /** Ancho aproximado de las flechas fijas (debe coincidir con `.nav-arrow { width }`). */
  private readonly NAV_ARROW_WIDTH_PX = 36;
  private readonly MENU_ITEM_SCROLL_PADDING_PX = 6;

  onMenuItemHover(index: number, event: MouseEvent): void {
    // Clear any existing timer
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }

    if (!this.isMobile) {
      this.dismissForeignOverlaysConditionally();
    }

    // Si cambia (o aún no se fija) el ítem padre, resetear el estado del 2.º/3.er
    // nivel. Sin esto, hoveredSubItem queda residual de un hover previo: al pasar
    // rápido a otro padre cuyo sub-ítem en el mismo índice tenga children, el
    // submenú anidado se abriría solo, sin que el usuario lo haya hover-eado.
    if (this.hoveredItem !== index) {
      this.hoveredSubItem = null;
      this.isNestedSubmenuActive = false;
      this.resetNestedSubmenuLayoutTransientState();
    }

    // Always update hovered item and recalculate position
    this.hoveredItem = index;
    const li = event.currentTarget as HTMLElement | null;
    this.lastHoveredMenuItemLi = li;
    this.submenuPositionLocked = false; // Unlock position for new menu
    this.calculateSubmenuPosition(event);

    if (li) {
      this.scrollMainMenuItemIntoViewIfNeeded(li);
    }
  }

  onMenuItemLeave(): void {
    // Start timer to hide menu after delay
    this.hideTimer = setTimeout(() => {
      this.hoveredItem = null;
      this.hoveredSubItem = null;
      this.lastHoveredMenuItemLi = null;
    }, this.HIDE_DELAY);
  }

  onSubItemHover(index: number, event: MouseEvent): void {
    // Clear any existing timer when hovering over submenu
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }

    if (!this.isMobile) {
      this.dismissForeignOverlaysConditionally();
    }

    this.hoveredSubItem = index;
    this.lastHoveredSubmenuLi = event.currentTarget as HTMLElement;
    if (this.menuItems[this.hoveredItem!]?.subMenu?.[index]?.children) {
      this.calculateNestedSubmenuPosition(event);
    } else {
      this.cancelNestedSubmenuViewportAdjust();
      this.nestedSubmenuMaxHeight = null;
    }
  }

  onSubItemLeave(): void {
    // Don't start timer if nested submenu is active
    // This prevents closing the nested submenu when navigating between levels
    if (this.isNestedSubmenuActive) {
      return; // Don't start any timer when nested submenu is active
    }

    // Only start timer if nested submenu is not active
    this.hideTimer = setTimeout(() => {
      // Double check that nested submenu is still not active before hiding
      if (!this.isNestedSubmenuActive) {
        this.hoveredSubItem = null;
      }
    }, this.HIDE_DELAY);
  }

  onSubmenuEnter(): void {
    // Clear any existing timer when entering submenu
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }

    if (!this.isMobile) {
      this.dismissForeignOverlaysConditionally();
    }

    // Lock the submenu position once user enters it
    this.submenuPositionLocked = true;
    // Mark submenu as active to maintain parent hover state
    this.isSubmenuActive = true;
  }


  /** `sessionStorage.iRegistro` truthy según JSON; falla cerrado si no hay valor o JSON inválido. */
  private isRegistroActivo(): boolean {
    if (typeof sessionStorage === "undefined") {
      return false;
    }
    try {
      const raw = sessionStorage.getItem("iRegistro");
      if (raw === null || raw === "") {
        return false;
      }
      const parsed: unknown = JSON.parse(raw);
      return Boolean(parsed);
    } catch {
      return false;
    }
  }

  onSubmenuLeave(): void {
    // Con el submenú portado a `body`, el puntero ya no está dentro del `li.menu-item`;
    // antes el DOM lo anidaba y el cierre dependía de salir de ese árbol. Sin cerrar aquí,
    // `hoveredItem` queda fijado y el submenú no desaparece.
    this.isSubmenuActive = false;
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.hideTimer = setTimeout(() => {
      this.hoveredItem = null;
      this.hoveredSubItem = null;
      this.lastHoveredMenuItemLi = null;
      this.isNestedSubmenuActive = false;
      this.submenuPositionLocked = false;
    }, this.HIDE_DELAY);
  }

  onNestedSubmenuEnter(): void {
    // Clear any existing timer when entering nested submenu
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    // Mark nested submenu as active to maintain second level hover state
    this.isNestedSubmenuActive = true;
  }

  onNestedItemHover(): void {
    // Clear any existing timer when hovering over nested submenu items
    // This follows the same pattern as onSubItemHover for second level
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    // Ensure nested submenu is marked as active
    this.isNestedSubmenuActive = true;
  }

  onNestedItemLeave(): void {
    // Don't start timer - this follows the same pattern as the second level
    // The nested submenu should remain open when navigating between items
    // Only onNestedSubmenuLeave should handle the closing logic
  }

  onNestedSubmenuLeave(): void {
    // Mark nested submenu as inactive
    this.isNestedSubmenuActive = false;
    // Don't start timer here - let onSubItemLeave handle it
    // This follows the same pattern as the second level
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["menuItems"]) {
      this.scheduleCheckScrollAfterMenuUpdate();
    }
  }

  ngAfterViewInit(): void {
    // Force check screen size after view init
    this.checkScreenSize();

    this.appendSubmenuPortalToBody();

    this.attachMenuScrollAndResizeObservers();

    // Check scroll state after view is initialized
    setTimeout(() => {
      this.checkScrollState();
      this.checkScreenSize(); // Double check screen size
    }, 100);

    // Force check after a longer delay to ensure content is loaded
    setTimeout(() => {
      this.checkScrollState();
      this.checkScreenSize(); // Final check
    }, 500);
  }

  ngOnDestroy(): void {
    this.menuWatchdog.stop("destroyed");
    this.destroy$.next();
    this.destroy$.complete();
    this.cancelNestedSubmenuViewportAdjust();
    // Clean up timer when component is destroyed
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.detachMenuScrollAndResizeObservers();
    this.detachMobileViewportMediaListener();
    this.removeSubmenuPortalFromBody();
  }

  private static matchesMaxWidthMedia(maxWidthPx: number): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    if (typeof window.matchMedia === 'function') {
      try {
        return window.matchMedia(`(max-width: ${maxWidthPx}px)`).matches;
      } catch {
        /* fall through */
      }
    }
    return window.innerWidth <= maxWidthPx;
  }

  private attachMobileViewportMediaListener(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`);
    } catch {
      return;
    }
    this.detachMobileViewportMediaListener();
    this.mobileViewportMql = mql;
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', this.onMobileViewportMqlChange);
    } else {
      mql.addListener(this.onMobileViewportMqlChange);
    }
  }

  private detachMobileViewportMediaListener(): void {
    const mql = this.mobileViewportMql;
    if (!mql) {
      return;
    }
    if (typeof mql.removeEventListener === 'function') {
      mql.removeEventListener('change', this.onMobileViewportMqlChange);
    } else {
      mql.removeListener(this.onMobileViewportMqlChange);
    }
    this.mobileViewportMql = null;
  }

  /**
   * El menú se pinta después de que Angular procese menuItems (p. ej. respuesta HTTP).
   * En single-spa, callbacks HTTP / rAF / ResizeObserver pueden quedar fuera de NgZone:
   * la vista no se repinta hasta un evento (p. ej. hover). NgZone.run + detectChanges
   * aseguran que las flechas aparezcan en cuanto haya datos y medición válida.
   */
  private scheduleCheckScrollAfterMenuUpdate(): void {
    this.ngZone.run(() => {
      queueMicrotask(() => {
        this.attachMenuScrollAndResizeObservers();
        this.checkScrollState();
        this.cdr.detectChanges();
        requestAnimationFrame(() => {
          this.checkScrollState();
          this.cdr.detectChanges();
          requestAnimationFrame(() => {
            this.checkScrollState();
            this.cdr.detectChanges();
            // Último intento por fuentes/layout que terminan después del API
            setTimeout(() => {
              this.checkScrollState();
              this.cdr.detectChanges();
            }, 100);
          });
        });
      });
    });
  }

  private attachMenuScrollAndResizeObservers(): void {
    if (!this.menuNav?.nativeElement || this.isMobile) {
      return;
    }

    const container = this.menuNav.nativeElement.closest(
      ".menu-container",
    ) as HTMLElement | null;
    if (!container) {
      return;
    }

    if (this.scrollableMenuContainer !== container) {
      this.detachMenuScrollListenersOnly();
      this.scrollableMenuContainer = container;
      container.addEventListener("scroll", this.onMenuContainerScroll, {
        passive: true,
      });
    }

    const menuList = this.menuNav.nativeElement.querySelector(
      ".menu-list",
    ) as HTMLElement | null;
    if (!menuList || typeof ResizeObserver === "undefined") {
      return;
    }

    if (!this.menuListResizeObserver) {
      this.menuListResizeObserver = new ResizeObserver(() => {
        this.ngZone.run(() => {
          this.checkScrollState();
          this.cdr.detectChanges();
        });
      });
    }
    this.menuListResizeObserver.disconnect();
    this.menuListResizeObserver.observe(menuList);
  }

  private detachMenuScrollListenersOnly(): void {
    if (this.scrollableMenuContainer) {
      this.scrollableMenuContainer.removeEventListener(
        "scroll",
        this.onMenuContainerScroll,
      );
      this.scrollableMenuContainer = null;
    }
  }

  private detachMenuScrollAndResizeObservers(): void {
    this.detachMenuScrollListenersOnly();
    this.menuListResizeObserver?.disconnect();
    this.menuListResizeObserver = null;
  }

  private appendSubmenuPortalToBody(): void {
    const el = this.submenuPortal?.nativeElement;
    if (!el || el.parentElement === document.body) {
      return;
    }
    this.renderer.appendChild(document.body, el);
  }

  private removeSubmenuPortalFromBody(): void {
    const el = this.submenuPortal?.nativeElement;
    if (!el?.parentNode) {
      return;
    }
    this.renderer.removeChild(el.parentNode, el);
  }

  /**
   * Reposiciona el submenú en scroll/resize cuando sigue abierto y la posición no está bloqueada
   * (usuario aún no entró al panel del submenú).
   */
  private refreshSubmenuPositionIfOpen(): void {
    if (
      this.isMobile ||
      this.hoveredItem === null ||
      this.submenuPositionLocked ||
      !this.lastHoveredMenuItemLi
    ) {
      return;
    }
    this.calculateSubmenuPositionFromMenuItemLi(this.lastHoveredMenuItemLi);
  }

  /**
   * Reubica/clampa el panel de tercer nivel si sigue abierto (p. ej. resize).
   */
  private refreshNestedSubmenuLayoutIfOpen(): void {
    if (
      this.isMobile ||
      this.hoveredItem === null ||
      this.hoveredSubItem === null ||
      !this.lastHoveredSubmenuLi
    ) {
      return;
    }
    const children = this.menuItems[this.hoveredItem]?.subMenu?.[this.hoveredSubItem]?.children;
    if (!children?.length) {
      return;
    }

    const li = this.lastHoveredSubmenuLi;
    if (!li.isConnected) {
      return;
    }

    this.nestedSubmenuMaxHeight = null;
    this.applyNestedSubmenuBaselineLayout(li);
    this.scheduleNestedSubmenuViewportAdjust(li);
  }

  private resetNestedSubmenuLayoutTransientState(): void {
    this.cancelNestedSubmenuViewportAdjust();
    this.nestedSubmenuMaxHeight = null;
    this.lastHoveredSubmenuLi = null;
  }

  private cancelNestedSubmenuViewportAdjust(): void {
    if (this.nestedSubmenuViewportAdjustRaf != null) {
      cancelAnimationFrame(this.nestedSubmenuViewportAdjustRaf);
      this.nestedSubmenuViewportAdjustRaf = null;
    }
  }

  /** Posición horizontal provisional + vertical alineada al ítem del 2.º nivel (se refina después en viewport). */
  private applyNestedSubmenuBaselineLayout(triggerLi: HTMLElement): void {
    const parentList = triggerLi.parentElement as HTMLElement;
    const itemRect = triggerLi.getBoundingClientRect();
    const parentListRect = parentList.getBoundingClientRect();
    const relativeY = itemRect.top - parentListRect.top - 8;

    const viewportWidth = window.innerWidth;
    const margin = 10;

    const openRightLeft = itemRect.width;
    const openLeftLeft = -this.NESTED_SUBMENU_WIDTH;

    const overflowsRight =
      itemRect.right + this.NESTED_SUBMENU_WIDTH > viewportWidth - margin;

    this.nestedSubmenuPosition = {
      top: relativeY + 8,
      left: overflowsRight ? openLeftLeft : openRightLeft
    };
  }

  private scheduleNestedSubmenuViewportAdjust(triggerLi: HTMLElement): void {
    const hi = this.hoveredItem;
    const hj = this.hoveredSubItem;
    if (typeof window === 'undefined') {
      return;
    }

    this.cancelNestedSubmenuViewportAdjust();
    this.nestedSubmenuViewportAdjustRaf = requestAnimationFrame(() => {
      this.nestedSubmenuViewportAdjustRaf = null;
      this.ngZone.run(() => {
        if (this.isMobile || this.hoveredItem !== hi || this.hoveredSubItem !== hj) {
          return;
        }
        if (!triggerLi.isConnected) {
          return;
        }
        this.adjustNestedSubmenuWithinViewport(triggerLi);
      });
    });
  }

  /**
   * Borde inferior del menú horizontal (viewport) más gap: el tercer nivel no debe superponer la barra.
   */
  private getNestedSubmenuViewportTopLimitPx(): number {
    const m = this.NESTED_VIEWPORT_MARGIN_PX;
    let container: HTMLElement | null = null;

    const navRoot = this.menuNav?.nativeElement;
    if (navRoot instanceof HTMLElement) {
      const fromNav = navRoot.closest('.menu-container');
      if (fromNav instanceof HTMLElement) {
        container = fromNav;
      }
    }

    if (!container && typeof document !== 'undefined') {
      const q = document.querySelector('.menu-container');
      if (q instanceof HTMLElement) {
        container = q;
      }
    }

    if (container) {
      const bottom = container.getBoundingClientRect().bottom;
      if (Number.isFinite(bottom)) {
        return Math.max(Math.round(bottom + this.NESTED_SUBMENU_MENU_BAR_GAP_PX), m);
      }
    }

    return m;
  }

  /**
   * Clamp vertical + `max-height` frente al viewport (abre «hacia arriba» si no cabe abajo).
   */
  private adjustNestedSubmenuWithinViewport(triggerLi: HTMLElement): void {
    const root = this.submenuPortal?.nativeElement;
    const nested = root?.querySelector('.nested-submenu') as HTMLElement | undefined;
    const submenuPane = triggerLi.closest('.submenu');

    if (!nested?.isConnected || !(submenuPane instanceof HTMLElement)) {
      return;
    }

    const m = this.NESTED_VIEWPORT_MARGIN_PX;
    const vh =
      typeof window !== 'undefined' && Number.isFinite(window.innerHeight)
        ? window.innerHeight
        : 800;

    const topLimitVp = this.getNestedSubmenuViewportTopLimitPx();
    const bottomLimit = vh - m;

    let maxVerticalBudget = Math.floor(bottomLimit - topLimitVp);
    if (maxVerticalBudget <= 0) {
      maxVerticalBudget = Math.max(1, Math.floor(bottomLimit - m));
    }

    let contentHeight = nested.scrollHeight;
    if (!Number.isFinite(contentHeight) || contentHeight <= 0) {
      contentHeight = maxVerticalBudget;
    }

    let panelH = Math.min(Math.ceil(contentHeight), Math.max(maxVerticalBudget, 1));
    panelH = Math.max(panelH, 1);

    this.nestedSubmenuMaxHeight = panelH;
    this.cdr.detectChanges();

    const refined = nested.scrollHeight;
    if (Number.isFinite(refined) && refined > 0) {
      panelH = Math.min(Math.ceil(refined), Math.max(maxVerticalBudget, 1));
      panelH = Math.max(panelH, 1);
      this.nestedSubmenuMaxHeight = panelH;
    }

    const smRect = submenuPane.getBoundingClientRect();
    const itemRect = triggerLi.getBoundingClientRect();

    let topVp = itemRect.top;
    if (topVp + panelH > bottomLimit) {
      topVp = bottomLimit - panelH;
    }
    if (topVp < topLimitVp) {
      topVp = topLimitVp;
    }

    const avail = bottomLimit - topVp;
    if (panelH > avail) {
      panelH = Math.max(1, Math.floor(avail));
      this.nestedSubmenuMaxHeight = panelH;
      this.cdr.detectChanges();
    }

    topVp = itemRect.top;
    if (topVp + panelH > bottomLimit) {
      topVp = bottomLimit - panelH;
    }
    if (topVp < topLimitVp) {
      topVp = topLimitVp;
    }

    this.nestedSubmenuPosition = {
      ...this.nestedSubmenuPosition,
      top: Math.round(topVp - smRect.top)
    };

    this.cdr.detectChanges();
  }

  // Check if scrolling is needed and update arrow visibility
  checkScrollState(): void {
    if (!this.menuNav || this.disableScroll) {
      this.showLeftArrow = false;
      this.showRightArrow = false;
      return;
    }

    // Get the scrollable container (menu-container)
    const scrollableContainer =
      this.menuNav.nativeElement.closest(".menu-container");
    if (!scrollableContainer) {
      return;
    }

    const scrollLeft = scrollableContainer.scrollLeft;
    const scrollWidth = scrollableContainer.scrollWidth;
    const clientWidth = scrollableContainer.clientWidth;

    // Calculate menu container position
    const containerRect = scrollableContainer.getBoundingClientRect();

    // Check if content overflows
    const needsScroll = scrollWidth > clientWidth;

    if (!needsScroll) {
      this.showLeftArrow = false;
      this.showRightArrow = false;
      this.canScrollLeft = false;
      this.canScrollRight = false;
      return;
    }

    // Show arrows based on scroll position with small tolerance
    const tolerance = 2; // Small tolerance for floating point precision
    const isAtStart = scrollLeft <= tolerance;
    const isAtEnd = scrollLeft >= scrollWidth - clientWidth - tolerance;

    // Show arrows based on position
    this.showLeftArrow = !isAtStart;
    this.showRightArrow = !isAtEnd;

    // Update scroll capabilities
    this.canScrollLeft = !isAtStart;
    this.canScrollRight = !isAtEnd;

    // Calculate arrow positions relative to menu container
    this.leftArrowPosition = {
      top: Math.round(containerRect.top),
      left: 0,
    };

    this.rightArrowPosition = {
      top: Math.round(containerRect.top),
      right: 0,
    };
  }

  // Scroll left
  scrollLeft(): void {
    this.trackMenuInteraction('scrollLeft', {
      interaction_type: 'click',
      element_type: 'button',
      element_name: 'Flecha Izquierda',
      component_name: 'Menu Horizontal'
    });

    if (!this.menuNav || !this.canScrollLeft) {
      return;
    }

    // Get the scrollable container (menu-container)
    const scrollableContainer =
      this.menuNav.nativeElement.closest(".menu-container");
    if (!scrollableContainer) {
      return;
    }

    const scrollAmount = scrollableContainer.clientWidth * 0.8; // Scroll 80% of visible width

    scrollableContainer.scrollBy({
      left: -scrollAmount,
      behavior: "smooth",
    });

    // Update state immediately and after scroll
    this.checkScrollState();
    setTimeout(() => {
      this.checkScrollState();
    }, 100);
    setTimeout(() => {
      this.checkScrollState();
    }, 300);
  }

  // Scroll right
  scrollRight(): void {
    this.trackMenuInteraction('scrollRight', {
      interaction_type: 'click',
      element_type: 'button',
      element_name: 'Flecha Derecha',
      component_name: 'Menu Horizontal'
    });

    if (!this.menuNav || !this.canScrollRight) {
      return;
    }

    // Get the scrollable container (menu-container)
    const scrollableContainer =
      this.menuNav.nativeElement.closest(".menu-container");
    if (!scrollableContainer) {
      return;
    }

    const scrollAmount = scrollableContainer.clientWidth * 0.8; // Scroll 80% of visible width

    scrollableContainer.scrollBy({
      left: scrollAmount,
      behavior: "smooth",
    });

    // Update state immediately and after scroll
    this.checkScrollState();
    setTimeout(() => {
      this.checkScrollState();
    }, 100);
    setTimeout(() => {
      this.checkScrollState();
    }, 300);
  }

  /**
   * Si el ítem del menú queda cortado por el borde del contenedor (o por las flechas),
   * desplaza el scroll horizontal para mostrar el texto completo.
   */
  private scrollMainMenuItemIntoViewIfNeeded(menuItemEl: HTMLElement): void {
    if (this.disableScroll || this.isMobile || !this.menuNav?.nativeElement) {
      return;
    }

    const container = this.menuNav.nativeElement.closest('.menu-container') as HTMLElement | null;
    if (!container || container.scrollWidth <= container.clientWidth + 1) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const itemRect = menuItemEl.getBoundingClientRect();

    const leftReserve = this.showLeftArrow ? this.NAV_ARROW_WIDTH_PX : 0;
    const rightReserve = this.showRightArrow ? this.NAV_ARROW_WIDTH_PX : 0;
    const pad = this.MENU_ITEM_SCROLL_PADDING_PX;

    const visibleLeft = containerRect.left + leftReserve + pad;
    const visibleRight = containerRect.right - rightReserve - pad;

    let delta = 0;
    if (itemRect.left < visibleLeft) {
      delta = itemRect.left - visibleLeft;
    } else if (itemRect.right > visibleRight) {
      delta = itemRect.right - visibleRight;
    }

    if (Math.abs(delta) < 1) {
      return;
    }

    const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
    const nextLeft = Math.min(maxScroll, Math.max(0, container.scrollLeft + delta));

    container.scrollTo({
      left: nextLeft,
      behavior: 'smooth'
    });

    setTimeout(() => this.checkScrollState(), 100);
    setTimeout(() => this.checkScrollState(), 350);
  }

  // Listen for scroll events
  @HostListener("scroll", ["$event"])
  onScroll(event: Event): void {
    this.checkScrollState();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
    setTimeout(() => {
      this.checkScrollState();
      this.refreshSubmenuPositionIfOpen();
      this.refreshNestedSubmenuLayoutIfOpen();
    }, 100);
  }

  // Public method to force check scroll state
  public forceCheckScrollState(): void {
    this.checkScrollState();
  }

  // Mobile menu methods
  checkScreenSize(): void {
    const wasMobile = this.isMobile;
    this.isMobile = MenuHorizontalComponent.matchesMaxWidthMedia(this.mobileBreakpoint);

    if (!this.isMobile) {
      this.isMobileMenuOpen = false;
    }

    if (wasMobile !== this.isMobile) {
      if (this.isMobile) {
        this.detachMenuScrollAndResizeObservers();
      } else {
        // *ngIf del menú desktop se actualiza en el siguiente ciclo; macrotask evita medir sin #menuNav.
        setTimeout(() => this.scheduleCheckScrollAfterMenuUpdate(), 0);
      }
    }
  }

  toggleMobileMenu(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const opening = !this.isMobileMenuOpen;
    this.trackMenuInteraction('toggleMobileMenu', {
      interaction_type: 'click',
      element_type: 'button',
      element_name: opening ? 'Abrir Menú Móvil' : 'Cerrar Menú Móvil',
      component_name: 'Menu Horizontal Mobile'
    });
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (opening) {
      this.dismissForeignOverlays();
    }
  }

  closeMobileMenu(): void {
    this.trackMenuInteraction('closeMobileMenu', {
      interaction_type: 'click',
      element_type: 'button',
      element_name: 'Cerrar Menú Móvil',
      component_name: 'Menu Horizontal Mobile'
    });
    this.dismissForeignOverlays();
    this.isMobileMenuOpen = false;
    this.mobileExpandedParentIndex = null;
    this.mobileExpandedSubParentIndex = null;
  }

  onMobileMenuItemClick(item: MenuItem): void {
    if (!item.subMenu || item.subMenu.length === 0) {
      this.closeMobileMenu();
    }
  }

  toggleMobileExpandedParent(index: number): void {
    if (this.mobileExpandedParentIndex === index) {
      this.mobileExpandedParentIndex = null;
      this.mobileExpandedSubParentIndex = null;
    } else {
      this.mobileExpandedParentIndex = index;
      this.mobileExpandedSubParentIndex = null;
    }
    this.cdr.markForCheck();
  }

  toggleMobileExpandedNested(parentIndex: number, subIndex: number): void {
    if (
      this.mobileExpandedParentIndex === parentIndex &&
      this.mobileExpandedSubParentIndex === subIndex
    ) {
      this.mobileExpandedSubParentIndex = null;
    } else {
      this.mobileExpandedParentIndex = parentIndex;
      this.mobileExpandedSubParentIndex = subIndex;
    }
    this.cdr.markForCheck();
  }

  // Manejar clic en items del menu principal
  onMenuItemClick(item: MenuItem, event: Event): void {
    event.preventDefault();
    const interaction: UIInteractionData = {
      interaction_type: 'click',
      element_type: 'button',
      element_name: item.label,
      element_id: item.redirect ?? '',
      component_name: 'Menu Horizontal'
    };
    if (item.redirect) {
      this.trackMenuInteraction('onMenuItemClick', interaction, () => this.redirectTo(item.redirect!));
    } else {
      this.trackMenuInteraction('onMenuItemClick', interaction);
    }
  }

  // Manejar clic en items del submenu
  onSubmenuItemClick(subItem: SubMenu, event: Event): void {
    event.preventDefault();
    const interaction: UIInteractionData = {
      interaction_type: 'click',
      element_type: 'button',
      element_name: subItem.label,
      element_id: subItem.redirect ?? '',
      component_name: 'Menu Horizontal Submenu'
    };
    if (subItem.redirect) {
      this.trackMenuInteraction('onSubmenuItemClick', interaction, () => this.redirectTo(subItem.redirect!));
    } else {
      this.trackMenuInteraction('onSubmenuItemClick', interaction);
    }
  }

  // Manejar clic en items del submenu anidado
  onNestedSubmenuItemClick(nestedItem: SubMenu, event: Event): void {
    event.preventDefault();
    const interaction: UIInteractionData = {
      interaction_type: 'click',
      element_type: 'button',
      element_name: nestedItem.label,
      element_id: nestedItem.redirect ?? '',
      component_name: 'Menu Horizontal Submenu Anidado'
    };
    if (nestedItem.redirect) {
      this.trackMenuInteraction('onNestedSubmenuItemClick', interaction, () => this.redirectTo(nestedItem.redirect!));
    } else {
      this.trackMenuInteraction('onNestedSubmenuItemClick', interaction);
    }
  }

  // Manejar clic en items del menu móvil
  onMobileMenuLinkClick(item: MenuItem, event: Event, index: number): void {
    if (item.subMenu && item.subMenu.length > 0) {
      this.trackMenuInteraction('onMobileMenuLinkClick-expand', {
        interaction_type: 'click',
        element_type: 'button',
        element_name: item.label,
        element_id: String(index),
        component_name: 'Menu Horizontal Mobile'
      });
      event.preventDefault();
      event.stopPropagation();
      this.toggleMobileExpandedParent(index);
      return;
    }
    this.trackMenuInteraction('onMobileMenuLinkClick', {
      interaction_type: 'click',
      element_type: 'button',
      element_name: item.label,
      element_id: item.redirect ?? '',
      component_name: 'Menu Horizontal Mobile'
    }, item.redirect
      ? () => {
          this.redirectTo(item.redirect!);
          this.closeMobileMenu();
        }
      : undefined);
    event.preventDefault();
    if (!item.redirect) {
      this.closeMobileMenu();
    }
  }

  // Manejar clic en items del submenu móvil
  onMobileSubmenuItemClick(
    subItem: SubMenu,
    event: Event,
    parentIndex: number,
    subIndex: number
  ): void {
    if (subItem.children && subItem.children.length > 0) {
      this.trackMenuInteraction('onMobileSubmenuItemClick-expand', {
        interaction_type: 'click',
        element_type: 'button',
        element_name: subItem.label,
        element_id: `${parentIndex}-${subIndex}`,
        component_name: 'Menu Horizontal Mobile Submenu'
      });
      event.preventDefault();
      event.stopPropagation();
      this.toggleMobileExpandedNested(parentIndex, subIndex);
      return;
    }
    this.trackMenuInteraction('onMobileSubmenuItemClick', {
      interaction_type: 'click',
      element_type: 'button',
      element_name: subItem.label,
      element_id: subItem.redirect ?? '',
      component_name: 'Menu Horizontal Mobile Submenu'
    }, subItem.redirect
      ? () => {
          this.redirectTo(subItem.redirect!);
          this.closeMobileMenu();
        }
      : undefined);
    event.preventDefault();
    if (!subItem.redirect) {
      this.closeMobileMenu();
    }
  }

  // Manejar clic en items del submenu anidado móvil
  onMobileNestedSubmenuItemClick(nestedItem: SubMenu, event: Event): void {
    event.preventDefault();
    const interaction: UIInteractionData = {
      interaction_type: 'click',
      element_type: 'button',
      element_name: nestedItem.label,
      element_id: nestedItem.redirect ?? '',
      component_name: 'Menu Horizontal Mobile Submenu Anidado'
    };
    if (nestedItem.redirect) {
      this.trackMenuInteraction('onMobileNestedSubmenuItemClick', interaction, () => {
        this.redirectTo(nestedItem.redirect!);
        this.closeMobileMenu();
      });
    } else {
      this.trackMenuInteraction('onMobileNestedSubmenuItemClick', interaction);
      this.closeMobileMenu();
    }
  }

  // Método para procesar las props recibidas del microfrontend
  public processMicrofrontendProps(): void {
    if (this.microfrontendProps) {
      this.usuario = this.microfrontendProps.usuario || "";
      this.rol = this.microfrontendProps.rol || "";
      this.token = this.microfrontendProps.token || "";
      this.whitelabel = this.microfrontendProps.whitelabel || "";
      this.onLogout = this.microfrontendProps.onLogout;
      this.Redirect = this.microfrontendProps.Redirect || null;
      this.urlSitio = this.microfrontendProps.UrlSitio || '';
      // Tema: prioridad Dominio (single-spa) y luego whitelabel
      this.applyTheme();
    }
  }

  // Método para manejar el logout
  handleLogout(): void {
    if (this.onLogout) {
      this.onLogout();
    }
  }

  // Método para obtener información del usuario
  getUserInfo(): { usuario: string; rol: string; whitelabel: string } {
    return {
      usuario: this.usuario,
      rol: this.rol,
      whitelabel: this.whitelabel,
    };
  }

  // Método para verificar si hay token válido
  hasValidToken(): boolean {
    return !!this.token && this.token.trim() !== "";
  }

  /**
   * Mapea `singleSpaProps.Dominio` a clave de tema (misma lógica que DominioOrigen / hostname).
   */
  private resolveThemeFromDominio(dominio: unknown): string | null {
    if (dominio === null || dominio === undefined) {
      return null;
    }
    const s = String(dominio).trim().toLowerCase();
    if (!s) {
      return null;
    }
    // Valores numéricos: DominioOrigen Iconstruye=1, Agilice=2, Redmat=3
    if (s === '1') {
      return 'ic';
    }
    if (s === '2') {
      return 'agilice';
    }
    if (s === '3') {
      return 'redmat';
    }
    if (s.includes('agilice')) {
      return 'agilice';
    }
    if (s.includes('redmat')) {
      return 'redmat';
    }
    if (s.includes('ic-corp') || s.includes('iccorp')) {
      return 'ic-corp';
    }
    if (s.includes('iconstruye')) {
      return 'ic';
    }
    return null;
  }

  // Método para aplicar tema: prioridad Dominio (props) y luego whitelabel
  private applyTheme(): void {
    const validThemes = ['ic', 'agilice', 'redmat', 'ic-corp'];
    const fromDominio = this.resolveThemeFromDominio(this.microfrontendProps?.Dominio);
    const fromWhitelabel = (this.whitelabel?.toLowerCase() || '').trim();
    const themeFromWhitelabel = validThemes.includes(fromWhitelabel) ? fromWhitelabel : null;
    const themeCandidate = fromDominio ?? themeFromWhitelabel ?? 'ic';
    this.currentTheme = validThemes.includes(themeCandidate) ? themeCandidate : 'ic';

    this.setDocumentTheme(this.currentTheme);
  }

  // Método para establecer el tema en el documento
  private setDocumentTheme(themeName: string): void {
    // Remover clases de tema anteriores
    document.documentElement.classList.remove(
      "theme-ic",
      "theme-agilice",
      "theme-redmat",
      "theme-ic-corp",
    );

    // Agregar nueva clase de tema
    document.documentElement.classList.add(`theme-${themeName}`);

    // También establecer como atributo data-theme
    document.documentElement.setAttribute('data-theme', themeName);


  }

  // Método para obtener información del tema actual
  getCurrentThemeInfo(): { theme: string; whitelabel: string; colors: any } {
    const themeColors = {
      ic: { bg: "#FF6A00", hover: "#FF7A23" },
      agilice: { bg: "#00A7E1", hover: "#00B4F2" },
      redmat: { bg: "#FF9016", hover: "#ED8716" },
      "ic-corp": { bg: "#64748B", hover: "#475569" },
    };

    return {
      theme: this.currentTheme,
      whitelabel: this.whitelabel,
      colors:
        themeColors[this.currentTheme as keyof typeof themeColors] ||
        themeColors["ic"],
    };
  }

  // Método para cambiar tema manualmente (útil para testing)
  changeTheme(themeName: string): void {
    const validThemes = ["ic", "agilice", "redmat", "ic-corp"];

    if (validThemes.includes(themeName)) {
      this.currentTheme = themeName;
      this.setDocumentTheme(themeName);
    }
  }

  debugMicrofrontendData(): void {
    // Debug method for console
  }

  /**
   * Consola de depuración: deja el menú vacío con mensaje (no hay dataset mock en el MF).
   */
  public usarDatosDePrueba(): void {
    this.ngZone.run(() => {
      this.menuItems = [];
      this.menuError =
        "Modo de prueba: sin datos de ejemplo en el microfrontend. Use recargarMenu() para volver al flujo normal.";
      this.isLoadingMenu = false;
      this.scheduleCheckScrollAfterMenuUpdate();
    });
  }

  /**
   * Vuelve a pedir `AutenticarNuevoProducto` (mismo flujo que al iniciar) y luego el menú.
   */
  public recargarMenu(): void {
    this.obtenerPermisosYCargarMenu();
  }

  /**
   * Obtiene información sobre el estado actual del menú
   */
  public getMenuInfo(): {
    totalItems: number;
    isUsingMockData: boolean;
    error: string | null;
    isLoading: boolean;
    menuLabels: string[];
    shellFallback: {
      key: string | null;
      attempts: number;
      maxAttempts: number;
      windowMs: number;
      minGapMs: number;
      wouldBlockReason: string | null;
    };
  } {
    const isUsingMockData = Boolean(this.menuError?.includes("Modo de prueba"));
    const fb = this.peekShellFallbackPolicyForDebug();

    return {
      totalItems: this.menuItems.length,
      isUsingMockData,
      error: this.menuError,
      isLoading: this.isLoadingMenu,
      menuLabels: this.menuItems.map((item) => item.label),
      shellFallback: {
        key: fb.key,
        attempts: fb.attempts,
        maxAttempts: fb.maxAttempts,
        windowMs: fb.windowMs,
        minGapMs: fb.minGapMs,
        wouldBlockReason: fb.wouldBlockReason,
      },
    };
  }

  // Debug method to check component state
  debugComponentState(): void {
    // Debug method for console
  }

  private calculateSubmenuPosition(event: MouseEvent): void {
    const li = (event.currentTarget ?? event.target) as HTMLElement | null;
    if (!li) {
      return;
    }
    this.calculateSubmenuPositionFromMenuItemLi(li);
  }

  private calculateSubmenuPositionFromMenuItemLi(menuItemLi: HTMLElement): void {
    // Don't recalculate if position is locked
    if (this.submenuPositionLocked) {
      return;
    }

    const rect = menuItemLi.getBoundingClientRect();
    const margin = 0;

    // Primer render: alineado al borde izquierdo del item, sin clamp con 200px.
    // CSS `width: max-content` pinta el panel según el contenido; evita wrap
    // artificial cuando abre a la izquierda por shrink-to-fit del viewport.
    this.submenuPosition = {
      top: rect.bottom - 2,
      left: Math.max(margin, rect.left + window.scrollX)
    };

    requestAnimationFrame(() => {
      const root = this.submenuPortal?.nativeElement;
      const submenuElement = root?.querySelector('.submenu') as HTMLElement | undefined;
      if (!submenuElement || this.submenuPositionLocked) {
        return;
      }

      const actualWidth = submenuElement.getBoundingClientRect().width;
      const viewportWidth = window.innerWidth;

      let nextLeft = rect.left + window.scrollX;
      if (nextLeft + actualWidth > viewportWidth - margin) {
        nextLeft = viewportWidth - actualWidth - margin;
      }
      nextLeft = Math.max(margin, nextLeft);

      if (Math.abs(nextLeft - this.submenuPosition.left) > 0.5) {
        this.ngZone.run(() => {
          this.submenuPosition = {
            ...this.submenuPosition,
            left: nextLeft
          };
          this.cdr.detectChanges();
        });
      }
    });
  }

  /** Ancho fijo del submenú anidado, alineado con `.nested-submenu { width }` en el SCSS. */
  private readonly NESTED_SUBMENU_WIDTH = 260;

  private calculateNestedSubmenuPosition(event: MouseEvent): void {
    const itemEl = (event.currentTarget ?? event.target) as HTMLElement;
    this.nestedSubmenuMaxHeight = null;
    this.lastHoveredSubmenuLi = itemEl;
    this.applyNestedSubmenuBaselineLayout(itemEl);
    this.scheduleNestedSubmenuViewportAdjust(itemEl);
    this.cdr.detectChanges();
  }

  private startMenuWatchdog(): void {
    this.menuWatchdog.start({
      getMenuItemsCount: () => this.menuItems.length,
      isLoading: () => this.isLoadingMenu,
      hasLocalStorageData: () => this.hasLocalStorageMenuDataForAnyKey(),
      countDomMenuItems: () => this.countVisibleDomMenuItems(),
      onRecovery: (level) => this.handleWatchdogRecovery(level),
    });
  }

  /** Hay al menos una entrada `menuasteric::*` legible con ítems (independiente de la clave actual). */
  private hasLocalStorageMenuDataForAnyKey(): boolean {
    return this.menuCacheService.leerPrimeraEntradaValida() !== null;
  }

  /** Cuenta ítems reales del menú en DOM (excluye skeletons de carga). */
  private countVisibleDomMenuItems(): number {
    if (typeof document === "undefined") {
      return 0;
    }
    return document.querySelectorAll(
      "li.menu-item:not(.menu-item-skeleton), li.mobile-menu-item:not(.mobile-menu-item-skeleton)",
    ).length;
  }

  private getIdentidadFromShellProps(): {
    IdUsuario: string;
    IdEmpresa: number;
    IdOrganizacion: number;
  } | null {
    const idUsuario = this.microfrontendProps.IdUsuario?.trim();
    const idEmpresa = this.microfrontendProps.IdEmpresa;
    const idOrg = this.microfrontendProps.IdOrg;
    if (!idUsuario || idEmpresa == null || idOrg == null) {
      return null;
    }
    return {
      IdUsuario: idUsuario,
      IdEmpresa: Number(idEmpresa),
      IdOrganizacion: Number(idOrg),
    };
  }

  /**
   * Clave de localStorage: JWT si existe; si no, props del shell + sufijo `-registro` si aplica.
   */
  private getClaveMenuCompleta(): string | null {
    const idPaisMenu = this.getIdPaisNormalizadoFromProps();
    let base = this.authService.getMenuCacheKey(idPaisMenu);
    if (!base) {
      const ident = this.getIdentidadFromShellProps();
      if (ident) {
        base = construirMenuCacheKey(ident, idPaisMenu);
      }
    }
    if (!base) {
      return null;
    }
    return this.isRegistroActivo() ? `${base}-registro` : base;
  }

  private leerMenuDesdeCacheLocal(): {
    cacheado: { raw: string; data: MenuComprador[] };
    clave: string | null;
    viaScan: boolean;
  } | null {
    const clave = this.getClaveMenuCompleta();
    if (clave) {
      const cacheado = this.menuCacheService.leer(clave);
      if (cacheado && cacheado.data.length > 0) {
        return { cacheado, clave, viaScan: false };
      }
    }
    const scan = this.menuCacheService.leerPrimeraEntradaValida();
    if (scan) {
      return {
        cacheado: { raw: scan.raw, data: scan.data },
        clave: scan.clave,
        viaScan: true,
      };
    }
    return null;
  }

  /** Pinta menú desde localStorage (clave JWT, props del shell o scan). */
  private pintarLocalStorageFallback(reason: string): boolean {
    const hit = this.leerMenuDesdeCacheLocal();
    if (!hit) {
      return false;
    }
    this.aplicarMenuItems(this.convertirMenuCompradorAMenuItem(hit.cacheado.data));
    const sinJwt = this.authService.getIdentidadUsuario() == null;
    console.info("[menu-swr] Menú pintado desde localStorage (fallback).", {
      reason,
      clave: hit.clave,
      viaScan: hit.viaScan,
      sinJwt,
      items: hit.cacheado.data.length,
    });
    return true;
  }

  private handleWatchdogRecovery(level: MenuWatchdogRecoveryLevel): void {
    const startedAt = Date.now();
    this.ngZone.run(() => {
      if (level === "reread-cache") {
        const hit = this.leerMenuDesdeCacheLocal();
        if (hit && hit.cacheado.data.length > 0) {
          this.menuItems = this.convertirMenuCompradorAMenuItem(hit.cacheado.data);
          this.isLoadingMenu = false;
          this.menuError = null;
          this.scheduleCheckScrollAfterMenuUpdate();
          const sinJwt = this.authService.getIdentidadUsuario() == null;
          console.warn("[menu-watchdog] Recuperación desde caché local.", {
            level,
            menuItemsAfter: this.menuItems.length,
            elapsedMs: Date.now() - startedAt,
            clave: hit.clave,
            viaScan: hit.viaScan,
            sinJwt,
          });
        } else {
          console.warn("[menu-watchdog] Recuperación caché sin datos legibles.", {
            level,
            claveMenu: this.getClaveMenuCompleta(),
            sinJwt: this.authService.getIdentidadUsuario() == null,
          });
        }
        return;
      }

      this.obtenerPermisosYCargarMenu();
      console.warn("[menu-watchdog] Recuperación vía recarga API.", {
        level,
        menuItemsAfter: this.menuItems.length,
        elapsedMs: Date.now() - startedAt,
      });
    });
  }

  /**
   * SWR: pinta caché local si existe; **siempre** revalida con el BFF.
   * Éxito: actualiza UI con la respuesta del API (aunque el payload sea equivalente al caché).
   * Error con caché: mantiene menú cacheado.
   * Error sin caché ni menú visible: deja el menú vacío.
   */
  private cargarMenuComprador(opciones?: { omitirCacheLocal?: boolean }): void {
    const menuVacio = this.menuItems.length === 0;
    if (menuVacio) {
      this.isLoadingMenu = true;
    }

    const idPaisMenu = this.getIdPaisNormalizadoFromProps();
    const claveMenu = this.getClaveMenuCompleta();
    const registro = this.isRegistroActivo();

    let cacheRaw: string | null = null;
    let cacheado: { raw: string; data: MenuComprador[] } | null = null;
    if (!opciones?.omitirCacheLocal) {
      const hit = this.leerMenuDesdeCacheLocal();
      if (hit) {
        cacheado = hit.cacheado;
        cacheRaw = hit.cacheado.raw;
        this.aplicarMenuItems(this.convertirMenuCompradorAMenuItem(hit.cacheado.data));
        console.info("[menu-swr] Menú pintado desde localStorage.", {
          items: hit.cacheado.data.length,
          clave: hit.clave ?? claveMenu,
          viaScan: hit.viaScan,
          sinJwt: this.authService.getIdentidadUsuario() == null,
        });
      } else if (menuVacio) {
        this.isLoadingMenu = true;
        console.info("[menu-swr] Sin caché local. Esperando respuesta API.", {
          clave: claveMenu,
        });
      }
    }

    this.menuError = null;

    console.info("[menu-swr] Llamando API para revalidar.", {
      idPais: idPaisMenu,
      registro,
      omitirCacheLocal: opciones?.omitirCacheLocal === true,
    });

    this.menuService
      .obtenerMenuComprador(idPaisMenu, registro)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (menuComprador: MenuCompradorResponse) => {
          this.ngZone.run(() => {
            this.emitBannerIdsIfPresent(menuComprador.bannerId);

            const menuNormalizado = normalizeMenuCompradorArray(menuComprador.menu);
            const frescoRaw = canonicalMenuRaw(menuNormalizado);
            const nuevosItems = this.convertirMenuCompradorAMenuItem(menuNormalizado);

            if (nuevosItems.length === 0 && this.menuItems.length > 0) {
              this.isLoadingMenu = false;
              console.warn("[menu-swr] API devolvió menú vacío. Manteniendo menú actual.", {
                itemsActuales: this.menuItems.length,
              });
              return;
            }

            if (cacheRaw !== null && cacheRaw === frescoRaw) {
              this.aplicarMenuItems(nuevosItems);
              console.info("[menu-swr] API confirmó datos sin cambios. UI sincronizada.", {
                items: menuNormalizado.length,
              });
              return;
            }

            this.menuCacheService.guardar(claveMenu, menuNormalizado);
            this.aplicarMenuItems(nuevosItems);
            console.info("[menu-swr] Menú actualizado desde API. localStorage sincronizado.", {
              itemsNuevos: menuNormalizado.length,
              teníaCache: cacheRaw !== null,
            });
          });
        },
        error: (err: unknown) => {
          this.ngZone.run(() => {
            if ((cacheRaw && cacheado) || this.menuItems.length > 0) {
              this.isLoadingMenu = false;
              this.menuError = null;
              console.warn("[menu-swr] Error API. Manteniendo menú visible.", {
                items: this.menuItems.length,
                desdeCache: !!(cacheRaw && cacheado),
                error: err,
              });
              return;
            }
            if (this.pintarLocalStorageFallback("api-error")) {
              this.menuError = null;
              console.warn("[menu-swr] Error API. Menú recuperado desde localStorage.", {
                items: this.menuItems.length,
                error: err,
              });
              return;
            }
            this.menuItems = [...this.mockMenuItems];
            this.menuError = `API no disponible. Mostrando datos de prueba (${this.menuItems.length} opciones)`;
            this.isLoadingMenu = false;
            this.scheduleCheckScrollAfterMenuUpdate();
            console.warn("[menu-swr] Error API sin menú disponible. Menú vacío.", { error: err });
          });
        },
      });
  }



  /** Reemplaza el menú en memoria y fuerza repintado sin activar skeleton. */
  private aplicarMenuItems(items: MenuItem[]): void {
    this.closeDesktopSubmenu();
    this.menuItems = items;
    this.menuError = null;
    this.isLoadingMenu = false;
    this.trackMenuPageView(items.length);
    this.scheduleCheckScrollAfterMenuUpdate();
  }

  private emitBannerIdsIfPresent(bannerId?: string[]): void {
    if (!Array.isArray(bannerId) || bannerId.length === 0) {
      return;
    }
    this.bannerIdsChange.emit(bannerId);
  }

  private convertirMenuCompradorAMenuItem(
    menuComprador: MenuComprador[],
  ): MenuItem[] {
    return menuComprador.map((item) => {
      const childNodes = readMenuCompradorChildren(item);
      return {
        label: item.label || item.nombre || '',
        redirect: item.redirect || item.url,
        subMenu: childNodes ? this.convertirSubMenu(childNodes) : undefined,
      };
    });
  }

  /**
   * Convierte los hijos de MenuComprador a SubMenu
   */
  private convertirSubMenu(hijos: MenuComprador[]): SubMenu[] {
    return hijos.map((hijo) => {
      const childNodes = readMenuCompradorChildren(hijo);
      return {
        label: hijo.label || hijo.nombre || '',
        redirect: hijo.redirect || hijo.url,
        children: childNodes ? this.convertirSubMenu(childNodes) : undefined,
      };
    });
  }

  redirectToHome(): void {
    this.trackMenuInteraction('redirectToHome', {
      interaction_type: 'click',
      element_type: 'button',
      element_name: 'Logo Home',
      component_name: 'Menu Horizontal Mobile'
    }, () => {
      this.dismissForeignOverlays();
      const homeUrl = `${window.location.origin}/includes/default.aspx`;
      this.shellIframeService.actualizarUrlSitio(homeUrl);
      window.location.href = homeUrl;
    });
  }

  /**
   * En escritorio, al interactuar por hover con el propio menú MF: ejecuta dismiss completo solo si algo
   * del shell parece abierto (evita CustomEvent/blur/pointer innecesarios).
   */
  private dismissForeignOverlaysConditionally(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    if (!MenuHorizontalComponent.foreignHostOverlaysAppearOpen()) {
      return;
    }
    this.dismissForeignOverlays();
  }

  /**
   * Pide al host que cierre overlays y fuerza el cierre de UI flotante de otros MF (SelectorApps, HeaderSite),
   * vía patrones conocidos sobre el DOM público (`menu-aplicaciones-new`, `app-selector-empresa`).
   *
   * **Contrato opcional:** el shell puede escuchar en `window` el evento `menu-asteric:dismiss-popovers`.
   */
  private dismissForeignOverlays(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    window.dispatchEvent(
      new CustomEvent(MenuHorizontalComponent.DISMISS_POPOVERS_EVENT, {
        bubbles: true,
        composed: true,
        detail: {
          source: 'menu-horizontal' as const,
          applicationsMenuHostKey: MenuHorizontalComponent.APPLICATIONS_MENU_HOST_KEY,
          headerSiteMicrofrontId: MenuHorizontalComponent.HEADER_SITE_MICROFRONT_ID
        }
      })
    );

    MenuHorizontalComponent.tryCloseSelectorAppsPopoverViaTrigger();
    MenuHorizontalComponent.tryCloseHeaderSiteSelectorEmpresaOverlaysViaTrigger();

    const appsHostEl = MenuHorizontalComponent.findApplicationsMenuHost();
    if (appsHostEl instanceof HTMLElement) {
      const active = document.activeElement;
      if (active instanceof HTMLElement && appsHostEl.contains(active)) {
        active.blur();
      }
    }

    const headerBar =
      MenuHorizontalComponent.queryHeaderSiteSelectorEmpresa('.div-container');
    if (headerBar instanceof HTMLElement) {
      const active = document.activeElement;
      if (active instanceof HTMLElement && headerBar.contains(active)) {
        active.blur();
      }
    }

    const pointerInit: PointerEventInit = {
      bubbles: true,
      cancelable: true,
      composed: true,
      view: window
    };

    try {
      if (typeof PointerEvent !== 'undefined') {
        document.dispatchEvent(new PointerEvent('pointerdown', pointerInit));
        document.dispatchEvent(new PointerEvent('pointerup', pointerInit));
      }
    } catch {
      /* ignore sintéticos no soportados en entorno raro */
    }
  }

  /** Cierra el dropdown de escritorio (portal) al elegir una opción o navegar. */
  private closeDesktopSubmenu(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.cancelNestedSubmenuViewportAdjust();
    this.nestedSubmenuMaxHeight = null;
    this.lastHoveredSubmenuLi = null;
    this.hoveredItem = null;
    this.hoveredSubItem = null;
    this.lastHoveredMenuItemLi = null;
    this.isSubmenuActive = false;
    this.isNestedSubmenuActive = false;
    this.submenuPositionLocked = false;
  }

  public redirectTo(url: string): void {
    this.dismissForeignOverlays();
    this.closeDesktopSubmenu();
    const fullUrl = this.shellIframeService.resolverUrlAbsoluta(url);
    this.microfrontendProps.Redirect(fullUrl);
    this.shellIframeService.registrarNavegacionPorMenu(fullUrl);
  }

  private cargarConfiguracionWhiteLabel(): void {
    this.ensureTokenPermisosYHacer(() => {
      const url = window.location.hostname;
      const dominio = this.obtenerDominioDesdeUrl(url);
      const ambiente = this.menuService.obtenerAmbienteOrigen(url);

      this.menuService
        .obtenerConfiguracionWhiteLabel(dominio, ambiente)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (config: EmpresaBrandig) => {
            this.ngZone.run(() => {
              this.empresaBranding = config;

              if (config.tema) {
                this.changeTheme(config.tema);
              }

              if (this.urlSitio) {
                const baseUrl = this.urlSitio.endsWith("/")
                  ? this.urlSitio.slice(0, -1)
                  : this.urlSitio;

                if (config.logoIsotipo) {
                  const isotipoPath = config.logoIsotipo.startsWith("/")
                    ? config.logoIsotipo
                    : `/${config.logoIsotipo}`;
                  this.logoIsotipoUrl = `${baseUrl}${isotipoPath}`;
                } else {
                  this.logoIsotipoUrl = this.construirUrlLogoFallback();
                }

                if (config.logoPlataforma) {
                  const plataformaPath = config.logoPlataforma.startsWith("/")
                    ? config.logoPlataforma
                    : `/${config.logoPlataforma}`;
                  this.logoPlataformaUrl = `${baseUrl}${plataformaPath}`;
                } else {
                  this.logoPlataformaUrl =
                    this.construirUrlLogoPlataformaFallback();
                }
              }
            });
          },
          error: (_err) => {
            this.ngZone.run(() => {
              this.aplicarLogosFallbackWhitelabel();
            });
          },
        });
    });
  }

  /** Logos locales por marca cuando el API de WhiteLabel no responde o viene incompleto. */
  private static readonly LOGO_ISOTIPO_FALLBACK: Record<
    "ic" | "agilice" | "redmat",
    string
  > = {
      ic: "logo-iconstruye-isotipo.png",
      agilice: "logo-agilice-isotipo.png",
      redmat: "logo-redmat-isotipo.png",
    };

  private static readonly LOGO_PLATAFORMA_FALLBACK: Record<
    "ic" | "agilice" | "redmat",
    string
  > = {
      ic: "logo-iconstruye.png",
      agilice: "logo-agilice_new.png",
      redmat: "logo-redmat.png",
    };

  private resolveMarcaWhitelabel(): "ic" | "agilice" | "redmat" {
    if (this.currentTheme === "agilice" || this.currentTheme === "redmat") {
      return this.currentTheme;
    }
    const dominio = this.obtenerDominioDesdeUrl(window.location.hostname);
    if (dominio === DominioOrigen.Agilice) {
      return "agilice";
    }
    if (dominio === DominioOrigen.Redmat) {
      return "redmat";
    }
    return "ic";
  }

  private construirUrlLogoFallback(): string | null {
    return this.construirUrlLogoDesdeMapa(
      MenuHorizontalComponent.LOGO_ISOTIPO_FALLBACK,
    );
  }

  private construirUrlLogoPlataformaFallback(): string | null {
    return this.construirUrlLogoDesdeMapa(
      MenuHorizontalComponent.LOGO_PLATAFORMA_FALLBACK,
    );
  }

  private construirUrlLogoDesdeMapa(
    logos: Record<"ic" | "agilice" | "redmat", string>,
  ): string | null {
    if (!this.urlSitio) {
      return null;
    }
    const baseUrl = this.urlSitio.endsWith("/")
      ? this.urlSitio.slice(0, -1)
      : this.urlSitio;
    const marca = this.resolveMarcaWhitelabel();
    return `${baseUrl}/img/${logos[marca]}`;
  }

  private aplicarLogosFallbackWhitelabel(): void {
    this.logoIsotipoUrl = this.construirUrlLogoFallback();
    this.logoPlataformaUrl = this.construirUrlLogoPlataformaFallback();
  }

  private obtenerDominioDesdeUrl(url: string): DominioOrigen {
    if (url.includes("agilice")) {
      return DominioOrigen.Agilice;
    }
    if (url.includes("redmat")) {
      return DominioOrigen.Redmat;
    }
    return DominioOrigen.Iconstruye;
  }

}
